import { create } from 'zustand';
import type { AxiosError } from 'axios';
import {
  type WorkStatusKey,
  readStoredStatus,
  writeStoredStatus,
  deriveStatusFromAttendance,
  resolveDisplayStatus,
  mergeWorkStatus,
} from '../lib/work-status';
import {
  getTodayAttendance, checkInAttendance, checkOutAttendance, cancelCheckOut, setWorkStatus,
  type DeemedDto,
} from '../api/attendance';
import { tLayout } from '../lib/layout-i18n';
import { punchIn as punchInLocal, punchOut as punchOutLocal, clearPunchOut as clearPunchOutLocal } from '../lib/attendance-local';

/**
 * ⑥ 상태바 근무 상태 스토어 — 2026-08-12 사용자 요구("상태를 클릭하면 출근·퇴근·회의 …").
 *
 * 두 출처를 합쳐 하나의 표시 상태를 만든다(합침 규칙은 `lib/work-status.resolveDisplayStatus`):
 *   - `local`      : 브라우저에 저장한 표시 상태. 하루 지나면 만료.
 *   - `server`     : 오늘 근태 기록에서 유도한 출근/퇴근. 다른 기기에서 찍어도 따라온다.
 *   - `serverStatus`: **서버 이력의 마지막 말**(2026-09-06 · 판 C). 회의중·자리비움도 이제
 *                    서버에 남는다 — 기기를 옮겨도 따라간다. 옛 서버면 null 이라 종전대로 돈다.
 *
 * 초기값을 localStorage 에서 **동기로** 읽는 이유: 새로고침 직후 "상태" 로 잠깐 돌아갔다가
 * 서버 응답이 와서 바뀌는 깜빡임을 없애기 위함. 서버 조회(`hydrate`)는 그 위에 덧씌운다.
 *
 * 실패 정책: 출근/퇴근은 **사실 기록**이라 서버 반영에 실패하면 표시도 바꾸지 않고 사유를
 * 남긴다(조용히 성공한 척하면 근태가 어긋난다).
 * ★★ 회의중·자리비움·외근·출장은 **바로 그려 놓고 뒤에서 보낸다** — 판 A 의 즉시 반응을
 *    지키려는 것이다(왕복을 기다리면 레일 칩이 잠깐 굳는다). 다만 **못 보냈으면 말한다**:
 *    조용히 성공한 척하면 다음 기기에서 상태가 없는 이유를 아무도 모른다.
 * ★ 옛 서버(404)는 실패가 아니다 — 그 서버에는 이 라우트가 없을 뿐이라 종전대로 로컬만 쓴다.
 */

interface WorkStatusState {
  /** 화면에 표시할 상태. null = 미설정("상태"). */
  status: WorkStatusKey | null;
  /** 로컬 저장 원본(합침 전). */
  local: WorkStatusKey | null;
  /** 서버 근태에서 유도한 값(합침 전). 조회 전이면 null. */
  server: WorkStatusKey | null;
  /**
   * 서버 **이력**의 마지막 말 (2026-09-06 · 판 C). 옛 서버는 안 주므로 null.
   * ★ null 은 「아무 상태도 아니다」가 아니라 **「모른다」** 다 — 합침 규칙이 그렇게 읽는다.
   */
  serverStatus: WorkStatusKey | null;
  /** 서버 왕복 중인 항목 — 해당 항목만 스피너/비활성 처리. */
  pending: WorkStatusKey | null;
  /** 마지막 실패 사유(사용자 표시용). 다음 선택 시 지워진다. */
  error: string | null;
  /**
   * 오늘 기록 시각(표시용 `HH:MM`). 조회 전이거나 미기록이면 null.
   * 2026-08-20 — 상태바 드롭다운이 "출근 09:12 / 퇴근 --:--" 를 보여 주기 위해 추가.
   * 판정에는 쓰지 않는다(판정은 종전대로 `deriveStatusFromAttendance`).
   */
  today: { checkIn: string | null; checkOut: string | null };
  /**
   * 오늘이 **간주 출근일**인가 — 승인된 출장·외근 (근태 D2, 2026-08-24).
   *
   * 결재가 끝나도 근태로 돌아오는 길이 없어 밖에서 일한 날이 "출근 기록 없음" 으로 남았다.
   * 이 값이 있으면 퇴근이 열리고, 화면이 **왜 출근으로 보는지**(출장·외근·목적지)를 말한다.
   * 옛 서버는 안 주므로 그때는 null — 종전과 똑같이 돈다.
   *
   * ★ `today` 를 건드리지 않고 칸을 따로 둔다. 여러 화면이 `today` 를 통째로 꺼내 쓰고 있어
   *   모양을 바꾸면 그 화면들이 함께 흔들린다.
   */
  deemed: DeemedDto | null;
  /**
   * 오늘 기록을 **실제로 읽어 냈는가**. 조회 전 · 조회 실패면 false.
   * 2026-08-23 — 퇴근 잠금("퇴근은 출근한 사람만")이 이 값을 본다. 모르는 상태와 "출근 안 함"
   * 은 다르다 — 조회가 실패했을 뿐인데 출근한 사람의 퇴근을 막으면 사실 기록이 늦어진다
   * (판정은 `lib/work-status.isCheckOutLocked` 한 곳에 모아 두었다).
   */
  hydrated: boolean;
  /** 오늘 근태 기록을 읽어 출근/퇴근을 교정한다. 실패는 조용히 무시(로컬 값 유지). */
  hydrate: () => Promise<void>;
  /** 상태 선택. 출근/퇴근은 서버 근태 라우트를, 나머지는 상태 라우트를 태운다. */
  select: (key: WorkStatusKey) => Promise<void>;
  /**
   * 퇴근 취소 — **같은 날 안에서만** (2026-09-06 · 판 C · 시안 질의 ⑧).
   * ★ 지난 날은 이 길이 아니다(그날 근로가 이미 셈에 들어갔다) — 「근태 정정」 결재로 간다.
   */
  cancelCheckOut: () => Promise<void>;
  /** 테스트 전용 초기화. */
  _resetForTests: () => void;
}

/** ISO 시각 → 표시용 `HH:MM`(로컬). 값이 없거나 파싱 불가면 null. */
function hhmm(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** axios 에러에서 서버가 준 code 를 꺼낸다(없으면 null). */
function errorCodeOf(err: unknown): string | null {
  const code = (err as AxiosError<{ code?: string }>)?.response?.data?.code;
  return typeof code === 'string' ? code : null;
}

const CHECK_IN_FAILED = '출근 기록에 실패했습니다. 잠시 후 다시 시도해 주세요.';
const CHECK_OUT_FAILED = '퇴근 기록에 실패했습니다. 잠시 후 다시 시도해 주세요.';
const NOT_CHECKED_IN = '오늘 출근 기록이 없습니다. 먼저 출근을 선택해 주세요.';

/**
 * ★ **늦게 온 응답이 새 선택을 덮지 않게** — 회의중 → 자리비움을 빠르게 누르면 두 요청이
 *   겹치고, 먼저 보낸 것이 나중에 도착할 수 있다. 마지막 것만 받아들인다.
 */
let statusSeq = 0;

export const useWorkStatusStore = create<WorkStatusState>((set, get) => ({
  status: readStoredStatus(),
  local: readStoredStatus(),
  server: null,
  serverStatus: null,
  pending: null,
  error: null,
  today: { checkIn: null, checkOut: null },
  deemed: null,
  hydrated: false,

  hydrate: async () => {
    try {
      const rec = await getTodayAttendance();
      const server = deriveStatusFromAttendance(rec);
      // 대시보드 근태 위젯은 아직 로컬 기록을 본다. select() 는 이미 미러링하지만 **다른 기기에서
      // 찍은 출근**은 조회로만 알 수 있어, 여기서도 서버 시각 그대로 로컬에 옮긴다. 안 옮기면
      // 위젯이 "출근 안 함" 으로 보여 퇴근 잠금이 엉뚱하게 걸린다. (둘 다 덮어쓰지 않는다.)
      if (rec.checkInAt) punchInLocal(new Date(rec.checkInAt));
      if (rec.checkOutAt) punchOutLocal(new Date(rec.checkOutAt));
      // 판 C — 서버 이력의 마지막 말. 옛 서버는 이 칸을 안 주므로 null(종전대로 돈다).
      const serverStatus = (rec.status ?? null) as WorkStatusKey | null;
      set((s) => ({
        server,
        serverStatus,
        status: mergeWorkStatus(s.local, server, serverStatus),
        today: { checkIn: hhmm(rec.checkInAt), checkOut: hhmm(rec.checkOutAt) },
        deemed: rec.deemed,
        hydrated: true,
      }));
    } catch {
      // 조회 실패는 표시에 영향 없음 — 로컬 값 그대로. 다음 선택/재진입에서 회복.
    }
  },

  select: async (key) => {
    if (get().pending) return; // 왕복 중 중복 선택 차단(출근 직후 퇴근 연타 등).
    set({ error: null });

    /**
     * 회의중 · 자리비움 · 외근 · 출장 — **바로 그리고 뒤에서 보낸다**.
     *
     * ★★ 왕복을 기다리면 레일 칩이 잠깐 굳는다(판 A 의 즉시 반응을 지킨다).
     * ★★ 그래도 **못 보냈으면 말한다** — 조용히 성공한 척하면 다른 기기에서 상태가
     *    안 보이는 이유를 아무도 모른다. 옛 서버(404)만 예외다(그 서버엔 라우트가 없다).
     * ★ 「출근」도 **이미 출근한 뒤라면** 여기로 온다 — 회의를 마치고 돌아온 것이지
     *   출근을 다시 찍는 것이 아니다(찍기는 아래 멱등 라우트가 한다).
     */
    const backToWork = key === 'work' && Boolean(get().today.checkIn);
    if ((key !== 'work' && key !== 'off') || backToWork) {
      const status = key as Exclude<WorkStatusKey, 'off'>;
      writeStoredStatus(status);
      set((s) => ({ local: status, serverStatus: status, status: mergeWorkStatus(status, s.server, status) }));
      const seq = ++statusSeq;
      try {
        const res = await setWorkStatus(status);
        if (seq !== statusSeq) return; // 늦게 온 응답이 새 선택을 덮지 않게.
        const next = (res.status ?? status) as WorkStatusKey;
        set((s) => ({ serverStatus: next, status: mergeWorkStatus(s.local, s.server, next) }));
      } catch (err) {
        if (seq !== statusSeq) return;
        // 옛 서버에는 이 라우트가 없다 — 실패가 아니라 «그 서버의 사정» 이라 조용히 넘어간다.
        const code = (err as AxiosError)?.response?.status;
        if (code !== 404 && code !== 405) {
          set({ serverStatus: null, error: tLayout('layout.workstatus.status_local_only') });
        }
      }
      return;
    }

    set({ pending: key });
    try {
      if (key === 'work') {
        const { attendance } = await checkInAttendance();
        // 대시보드 근태 위젯이 아직 로컬 기록을 본다 — 같은 사실이 두 화면에서 어긋나지 않게 미러링.
        punchInLocal();
        writeStoredStatus('work');
        const server = deriveStatusFromAttendance(attendance) ?? 'work';
        set({
          // ★ 출근은 이력을 **다시 여는** 일이다 — 어제 남은 「자리비움」이 따라오면 안 된다.
          local: 'work', server, serverStatus: null, status: resolveDisplayStatus('work', server), pending: null,
          today: { checkIn: hhmm(attendance.checkInAt), checkOut: hhmm(attendance.checkOutAt) },
          hydrated: true,
        });
      } else {
        const attendance = await checkOutAttendance();
        punchOutLocal();
        writeStoredStatus('off');
        const server = deriveStatusFromAttendance(attendance) ?? 'off';
        set({
          // ★ 퇴근하면 상태는 끝난다 — 「회의중인 채로 퇴근」이 남으면 상태바가 거짓말을 한다.
          local: 'off', server, serverStatus: null, status: resolveDisplayStatus('off', server), pending: null,
          today: { checkIn: hhmm(attendance.checkInAt), checkOut: hhmm(attendance.checkOutAt) },
          hydrated: true,
        });
      }
    } catch (err) {
      const code = errorCodeOf(err);
      set({
        pending: null,
        error: code === 'NOT_CHECKED_IN'
          ? NOT_CHECKED_IN
          : key === 'work' ? CHECK_IN_FAILED : CHECK_OUT_FAILED,
      });
    }
  },

  /**
   * 퇴근 취소 — **같은 날 안에서만** (2026-09-06 · 판 C).
   *
   * ★★★ 되돌리면 **다시 근무 중**이다. 상태를 안 되돌리면 「퇴근」 칩이 남은 채 퇴근 시각만
   *    사라져, 화면이 스스로 어긋난 말을 한다.
   * ★ 실패하면 **아무것도 바꾸지 않는다** — 사실 기록을 지우는 일이라 성공한 척하면 안 된다.
   */
  cancelCheckOut: async () => {
    if (get().pending) return;
    set({ pending: 'off', error: null });
    try {
      const { attendance, status } = await cancelCheckOut();
      // 위젯이 아직 로컬 기록을 본다 — 서버에서 지운 퇴근이 거기 남으면 오늘이 두 가지가 된다.
      clearPunchOutLocal();
      /**
       * ★★★ **되돌리면 «퇴근 앞의 상태» 로 돌아간다** — 회의중이던 사람이 잘못 퇴근을 찍고
       *    취소했으면 다시 회의중이다. 무조건 `work` 로 적으면 그 사람의 상태를 **화면이
       *    지어내는** 것이 된다. 서버가 이력을 되짚어 답하고(취소는 그 앞의 퇴근을 무른다),
       *    옛 서버·모르는 경우만 `work` 로 떨어진다(출근 기록이 그렇게 말한다).
       */
      const back = (status ?? 'work') as WorkStatusKey;
      const server = deriveStatusFromAttendance(attendance);
      writeStoredStatus(back);
      set({
        local: back, server, serverStatus: status as WorkStatusKey | null,
        status: mergeWorkStatus(back, server, status as WorkStatusKey | null), pending: null,
        today: { checkIn: hhmm(attendance.checkInAt), checkOut: hhmm(attendance.checkOutAt) },
        hydrated: true,
      });
    } catch {
      set({ pending: null, error: tLayout('layout.workstatus.cancel_off_failed') });
    }
  },

  _resetForTests: () => set({
    status: null, local: null, server: null, serverStatus: null, pending: null, error: null,
    today: { checkIn: null, checkOut: null }, deemed: null, hydrated: false,
  }),
}));
